import {
  CART_FRAGMENT,
  ERROR_FRAGMENT,
  ORDER_FRAGMENT,
  PURCHASE_PREMIUM_FRAGMENT,
  USER_COUPON_FRAGMENT,
} from '@/services/graphql/fragments';
import gql from 'graphql-tag';

/** 购物车订单行字段 */
const shoppingCartOrderLineFields = `
  fragment OrderLine on OrderLine {
    id
    quantity
    unitPrice
    productVariant {
      ...ProductVariant
    }
    customFields {
      purchasePattern
    }
  }

  fragment ProductVariant on ProductVariant {
    id
    sku
    price
    name
    exclusionGroupsTypeName
    isCanPurchase
    options {
      code
      name
    }
    pointExchange {
      points
    }
    featuredAsset {
      source
    }
    isThereAnyStock
    product {
      ...Product
    }
    customFields {
      virtualTargetType
    }
  }

  fragment Product on Product {
    id
    name
    enabled
    customFields {
      putOnSaleType
      putOnSaleTime
    }
    productTotalStock
    productPurchasePermission {
      id
      productId
      isMembershipPlanPurchase
      membershipPlans {
        id
      }
      guideMembershipPlanId
    }
    memberPriceActivityAmount {
      minDiscount
      maxDiscount
      minMemberPriceAmount
      maxMemberPriceAmount
      memberPriceProductVariant {
        memberPriceAmount
        productVariantId
        memberDiscount
        discountType
      }
    }
    featuredAsset {
      source
    }
    customFields {
      price
      markingPrice
      limitType
      limitCount
      putOnSaleType
      putOnSaleTime
      isVipProduct
      isHiddenCart
      virtualTargetType
    }
    pointsExchangeInfo {
      allowPurchaseAtOriginalPrice
    }
  }
`;

/** 购物车订单基础字段 */
const shoppingCartOrderBaseFields = `
  id
  shipping
  customFields {
    orderPromotionResult {
      promResult {
        orderTotalPrice
      }
    }
  }
  lines {
    ...OrderLine
  }
`;

/**
 * 获取购物车和预下单信息（包含自提信息）
 */
export const fetchGetShoppingCartWithPickupDocument = gql`
  query getShoppingCart(
    $isRemoveMarkUp: Boolean
    $isUseMember: Boolean
    $isUseShoppingCredit: Boolean
    $isRemoveGuessLike: Boolean
  ) {
    getShoppingCart(
      isRemoveMarkUp: $isRemoveMarkUp
      isUseMember: $isUseMember
      isUseShoppingCredit: $isUseShoppingCredit
      isRemoveGuessLike: $isRemoveGuessLike
    ) {
      shoppingTrolley {
        ...Order
      }
      outrightPurchase {
        ...Order
      }
    }
  }
  fragment Order on Order {
    ${shoppingCartOrderBaseFields}
    orderPickup {
      deliveryType
    }
  }
  ${shoppingCartOrderLineFields}
`;

/**
 * 获取购物车和预下单信息（不包含自提信息）
 */
export const fetchGetShoppingCartWithoutPickupDocument = gql`
  query getShoppingCart(
    $isRemoveMarkUp: Boolean
    $isUseMember: Boolean
    $isUseShoppingCredit: Boolean
    $isRemoveGuessLike: Boolean
  ) {
    getShoppingCart(
      isRemoveMarkUp: $isRemoveMarkUp
      isUseMember: $isUseMember
      isUseShoppingCredit: $isUseShoppingCredit
      isRemoveGuessLike: $isRemoveGuessLike
    ) {
      shoppingTrolley {
        ...Order
      }
      outrightPurchase {
        ...Order
      }
    }
  }
  fragment Order on Order {
    ${shoppingCartOrderBaseFields}
  }
  ${shoppingCartOrderLineFields}
`;

/**
 * 获取购物车和预下单信息
 * @deprecated 请使用 fetchGetShoppingCartWithPickupDocument 或 fetchGetShoppingCartWithoutPickupDocument
 */
export const fetchGetShoppingCartDocument = fetchGetShoppingCartWithPickupDocument;

/** 活跃订单基础字段 */
const activeOrderBaseFields = `
  id
  code
  state
  active
  updatedAt
  orderPlacedAt
  lines {
    id
    quantity
    totalQuantity
    unitPrice
    featuredAsset {
      preview
    }
    productVariant {
      id
      sku
      price
      name
      pointExchange {
        points
        cash
        exchangeTotal
        exchangeCount
      }
      options {
        code
        name
      }
      featuredAsset {
        source
      }
      virtualTarget {
        ... on Coupon {
          id
          cname: name
          cstate: state
          enable
        }
        ... on MembershipPlan {
          id
          name
          state
        }
      }
      product {
        id
        name
        enabled
        customFields {
          isVipProduct
          unit
        }
        featuredAsset {
          source
        }
        customFields {
          price
          markingPrice
          unit
          limitType
          limitCount
          putOnSaleType
          putOnSaleTime
        }
        productTotalStock
      }
      customFields {
        virtualTargetType
      }
    }
    customFields {
      purchasePattern
    }
  }
  totalQuantity
  total
  shipping
  customFields {
    buyType
    orderPromotionResult {
      promResult {
        orderTotalPrice
        orderTotalPoints
      }
    }
  }
`;

/**
 * 根据类型查询活跃订单（包含自提信息）
 */
export const fetchActiveOrderByTypeWithPickupDocument = gql`
  query getActiveOrderByType($type: OrderPurchaseType!, $isRemoveMarkUp: Boolean, $isRemoveGuessLike: Boolean) {
    getActiveOrderByType(type: $type, isRemoveMarkUp: $isRemoveMarkUp, isRemoveGuessLike: $isRemoveGuessLike) {
      ${activeOrderBaseFields}
      orderPickup {
        deliveryType
      }
    }
  }
`;

/**
 * 根据类型查询活跃订单（不包含自提信息）
 */
export const fetchActiveOrderByTypeWithoutPickupDocument = gql`
  query getActiveOrderByType($type: OrderPurchaseType!, $isRemoveMarkUp: Boolean, $isRemoveGuessLike: Boolean) {
    getActiveOrderByType(type: $type, isRemoveMarkUp: $isRemoveMarkUp, isRemoveGuessLike: $isRemoveGuessLike) {
      ${activeOrderBaseFields}
    }
  }
`;

/**
 * 根据类型查询活跃订单
 * @deprecated 请使用 fetchActiveOrderByTypeWithPickupDocument 或 fetchActiveOrderByTypeWithoutPickupDocument
 */
export const fetchActiveOrderByTypeDocument = fetchActiveOrderByTypeWithPickupDocument;

/**
 * 获取购物车优惠信息
 */
export const fetchOrderPromotionResultDocument = gql`
  query orderPromotionResult($orderIds: [ID!]!) {
    orderPromotionResult(orderIds: $orderIds) {
      id
      createdAt
      updatedAt
      isCartShowActuallyPaidGift
      isCartShowFullDiscountPresentGift
      isCartShowSelectiveGift
      promResult {
        orderId
        discountAmount
        orderTotalPrice
        orderTotalPoints
        shoppingCreditsClaim
        shoppingCreditsDeduction
        disableShoppingCredits
        promLineResults {
          promInstanceId
          tags
          orderLines {
            orderLineId
            discountCount
            discountAmount
            skuId
            discount
            displayInThisGroup
            points
            cash
            wishBoxActivityBoxType
          }
          priority
          type
          shouldGroup
          description
          promTime
          promContent
          promOverview
          discountType
          discountAmount
          discountCount
          meetCondition
          superimposeType
          superimposeTypes
          orderLineBlindBoxMap {
            orderLineId
            blindBoxOrderBuyId
            blindBoxOrderBuyPrice
          }
          gifts {
            promInstanceId
            giftType
            items {
              giftId
              productId
              skuId
              count
              name
              giftPrice
              price
              selected
              isAvailable
              minimumStr
              ladderLevel
            }
          }
          coupons {
            couponId
            selected
            price
            autoSelected
          }
        }
        orderLinePromResults {
          orderLineId
          skuId
          count
          price
          discountAmount
          discountDetails {
            promInstanceId
            type
            superimposeType
            superimposeTypes
            discountCount
            discountAmount
          }
        }
        leftOrderLines {
          skuId
          lineId
          count
          price
          discountAmount
        }
        discountByTypes {
          type
          discountAmount
        }
        gifts {
          promInstanceId
          giftType
          items {
            giftId
            productId
            skuId
            count
            name
            giftPrice
            price
            selected
            isAvailable
            minimumStr
          }
        }
        coupons {
          couponId
          selected
          price
        }
        disableMember
      }
    }
  }
`;

/**
 * 清除所有购物车 - 直接购买场景
 */
export const fetchRemoveAllOrderLinesDocument = gql`
  mutation removeAllOrderLines {
    removeAllOrderLines {
      ...Cart
      ...ErrorResult
    }
  }
  ${CART_FRAGMENT}
  ${ERROR_FRAGMENT}
`;

/**
 * 添加至购物车
 */
export const fetchAddItemToOrderByOrderTypeDocument = gql`
  mutation addItemToOrderByOrderType(
    $productVariantId: ID!
    $quantity: Int!
    $type: OrderPurchaseType!
    $buyType: OrderBuyType
  ) {
    addItemToOrderByOrderType(
      productVariantId: $productVariantId
      quantity: $quantity
      type: $type
      buyType: $buyType
    ) {
      ... on Order {
        id
      }
    }
  }
`;

/**
 * 修改购物车规格和数量
 */
export const fetchUpdateShoppingCartSkuDocument = gql`
  mutation updateShoppingCartSku($orderLineId: ID!, $productVariantId: ID!, $quantity: Int!) {
    updateShoppingCartSku(
      orderLineId: $orderLineId
      productVariantId: $productVariantId
      quantity: $quantity
    ) {
      ...Cart
    }
  }
  ${CART_FRAGMENT}
`;

/**
 * 修改指定项购物车数量
 */
export const fetchAdjustOrderLineDocument = gql`
  mutation adjustOrderLineByOrderType(
    $orderLineId: ID!
    $quantity: Int!
    $type: OrderPurchaseType!
  ) {
    adjustOrderLineByOrderType(orderLineId: $orderLineId, quantity: $quantity, type: $type) {
      id
    }
  }
`;

/**
 * 删除指定项购物车
 */
export const fetchRemoveOrderLineDocument = gql`
  mutation removeOrderLineByOrderType($orderLineIds: [ID!]!, $type: OrderPurchaseType!) {
    removeOrderLineByOrderType(orderLineIds: $orderLineIds, type: $type) {
      id
    }
  }
`;

/**
 * 购物车结算
 */
export const fetchShoppingCartSettlementDocument = gql`
  mutation shoppingCartSettlement($input: [SettlementProduct!]!) {
    shoppingCartSettlement(input: $input) {
      id
      lines {
        id
      }
    }
  }
`;

/**
 * 获取发货方式
 */
export const fetchEligibleShippingMethodsByOrderTypeDocument = gql`
  query eligibleShippingMethodsByOrderType($type: OrderPurchaseType!) {
    eligibleShippingMethodsByOrderType(type: $type) {
      id
      name
      description
      price
      priceWithTax
      metadata
    }
  }
`;

/**
 * 设置发货方式
 */
export const setShippingMethodByOrderTypeDocument = gql`
  mutation setShippingMethodByOrderType($type: OrderPurchaseType!, $shippingMethodId: ID!) {
    setShippingMethodByOrderType(type: $type, shippingMethodId: $shippingMethodId) {
      id
    }
  }
`;

/**
 * 设置下单自定义字段
 */
export const fetchSetOrderCustomFieldsDocument = gql`
  mutation setOrderCustomFields($input: UpdateOrderInput!) {
    setOrderCustomFields(input: $input) {
      ...Order
      ...Cart
      ...ErrorResult
    }
  }
  ${CART_FRAGMENT}
  ${ERROR_FRAGMENT}
  ${ORDER_FRAGMENT}
`;

/**
 * 获取微信配置信息 - h5环境
 */
export const fetchGetJsConfigDocument = gql`
  mutation getJsConfig($url: String!) {
    getJsConfig(url: $url) {
      appId
      timestamp
      nonceStr
      signature
      officialAccountCode
    }
  }
`;

/**
 * 查询当前订单中的商品是否参与了会员价活动
 */
export const fetchGetOrderIsIncludeMemberPriceDocument = gql`
  query getOrderIsIncludeMemberPrice($orderId: ID!) {
    getOrderIsIncludeMemberPrice(orderId: $orderId)
  }
`;

/**
 * 查询结算订单中的商品是否都是限购
 */
export const fetchCheckProductCanCheckoutDocument = gql`
  query checkProductCanCheckout($skuIds: [ID!]!, $skuQuantities: [SkuQuantityInput!]) {
    checkProductCanCheckout(skuIds: $skuIds, skuQuantities: $skuQuantities) {
      canCheckout
      reason
      errorDetail {
        type
        skuIds
        message
      }
    }
  }
`;

/**
 * 取消购物金抵扣
 */
export const fetchCancelShoppingCreditsDocument = gql`
  mutation cancelShoppingCredits($orderId: ID!) {
    cancelShoppingCredits(orderId: $orderId) {
      id
    }
  }
`;

/**
 * 申请购物金抵扣
 */
export const fetchApplyShoppingCreditsDocument = gql`
  mutation applyShoppingCredits($orderId: ID!) {
    applyShoppingCredits(orderId: $orderId) {
      id
    }
  }
`;

/**
 * 查询购物车页面是否有活动倒计时
 */
export const fetchActivityCountdownByCartDocument = gql`
  query activityCountdownByCart {
    activityCountdownByCart {
      activityCountdown {
        id
        createdAt
        activityId
        promotionId
        activityType
        name
        countdownTime
        priority
      }
      activityTag
    }
  }
`;

/**
 * 获取 可用/不可用 优惠券列表
 */
export const fetchGetActiveCouponsDocumenet = gql`
  query getActiveCoupons($orderId: ID) {
    getActiveCoupons(orderId: $orderId) {
      availableCoupons {
        ...UserCoupon
      }
      couponsAreNotAvailable {
        ...UserCoupon
      }
      lackOfExchangeGoodsCoupons {
        ...UserCoupon
      }
    }
  }
  ${USER_COUPON_FRAGMENT}
`;

/**
 * 根据订单id查询换购商品
 */
export const fetchMarkUpByOrderDocument = gql`
  query markUpByOrder($orderId: ID!) {
    markUpByOrder(orderId: $orderId) {
      ...PurchasePremium
    }
  }
  ${PURCHASE_PREMIUM_FRAGMENT}
`;

/**
 * 根据订单id查询赠品
 */
export const fetchFreeGiftByOrderDocument = gql`
  query freeGiftByOrder($orderId: ID!) {
    freeGiftByOrder(orderId: $orderId) {
      id
      name
      status
      product {
        id
        name
        enabled
        customFields {
          isVipProduct
          unit
        }
        optionGroups {
          id
          code
          name
          customFields {
            priority
          }
          options {
            id
            code
            name
            customFields {
              priority
            }
          }
        }
        variants {
          id
          name
          options {
            code
            name
            id
            group {
              id
              code
              name
            }
          }
          featuredAsset {
            source
          }
          isThereAnyStock
          price
          priceWithTax
          sku
        }
        featuredAsset {
          source
        }
        customFields {
          price
          markingPrice
        }
      }
    }
  }
`;

/**
 * 订单确认页附加数据（5 合 1）- 直接购买场景
 * 合并: orderPromotionResult + freeGiftByOrder + markUpByOrder + getActiveCoupons + getOrderIsIncludeMemberPrice
 *
 * 注意：不包含 checkProductCanCheckout，因为直接购买在商品详情页已校验限购
 */
export const fetchRegularOrderExtrasDocument = gql`
  query RegularOrderExtras($orderId: ID!) {
    # 订单促销优惠结果 - 与 fetchOrderPromotionResultDocument 保持一致
    orderPromotionResult(orderIds: [$orderId]) {
      id
      createdAt
      updatedAt
      isCartShowActuallyPaidGift
      isCartShowFullDiscountPresentGift
      isCartShowSelectiveGift
      promResult {
        orderId
        discountAmount
        orderTotalPrice
        orderTotalPoints
        shoppingCreditsClaim
        shoppingCreditsDeduction
        disableShoppingCredits
        promLineResults {
          promInstanceId
          tags
          orderLines {
            orderLineId
            discountCount
            discountAmount
            skuId
            discount
            displayInThisGroup
            points
            cash
            wishBoxActivityBoxType
          }
          priority
          type
          shouldGroup
          description
          promTime
          promContent
          promOverview
          discountType
          discountAmount
          discountCount
          meetCondition
          superimposeType
          superimposeTypes
          orderLineBlindBoxMap {
            orderLineId
            blindBoxOrderBuyId
            blindBoxOrderBuyPrice
          }
          gifts {
            promInstanceId
            giftType
            items {
              giftId
              productId
              skuId
              count
              name
              giftPrice
              price
              selected
              isAvailable
              minimumStr
              ladderLevel
            }
          }
          coupons {
            couponId
            selected
            price
            autoSelected
          }
        }
        orderLinePromResults {
          orderLineId
          skuId
          count
          price
          discountAmount
          discountDetails {
            promInstanceId
            type
            superimposeType
            superimposeTypes
            discountCount
            discountAmount
          }
        }
        leftOrderLines {
          skuId
          lineId
          count
          price
          discountAmount
        }
        discountByTypes {
          type
          discountAmount
        }
        gifts {
          promInstanceId
          giftType
          items {
            giftId
            productId
            skuId
            count
            name
            giftPrice
            price
            selected
            isAvailable
            minimumStr
          }
        }
        coupons {
          couponId
          selected
          price
        }
        disableMember
      }
    }

    # 赠品信息 - 与 fetchFreeGiftByOrderDocument 保持一致
    freeGiftByOrder(orderId: $orderId) {
      id
      name
      status
      product {
        id
        name
        enabled
        customFields {
          isVipProduct
          unit
        }
        optionGroups {
          id
          code
          name
          customFields {
            priority
          }
          options {
            id
            code
            name
            customFields {
              priority
            }
          }
        }
        variants {
          id
          name
          options {
            code
            name
            id
            group {
              id
              code
              name
            }
          }
          featuredAsset {
            source
          }
          isThereAnyStock
          price
          priceWithTax
          sku
        }
        featuredAsset {
          source
        }
        customFields {
          price
          markingPrice
        }
      }
    }

    # 加价购信息 - 使用 PURCHASE_PREMIUM_FRAGMENT
    markUpByOrder(orderId: $orderId) {
      ...PurchasePremium
    }

    # 优惠券列表 - 与 fetchGetActiveCouponsDocumenet 保持一致
    getActiveCoupons(orderId: $orderId) {
      availableCoupons {
        ...UserCoupon
      }
      couponsAreNotAvailable {
        ...UserCoupon
      }
      lackOfExchangeGoodsCoupons {
        ...UserCoupon
      }
    }

    # 订单是否包含会员价商品
    getOrderIsIncludeMemberPrice(orderId: $orderId)
  }
  ${PURCHASE_PREMIUM_FRAGMENT}
  ${USER_COUPON_FRAGMENT}
`;

/**
 * 获取开通年卡会员后可以优惠多少结算金额
 */
export const fetchCalculateMemberDiscountDifferenceDocument = gql`
  query calculateMemberDiscountDifference($orderId: ID!) {
    calculateMemberDiscountDifference(orderId: $orderId) {
      discountDifference
      memberShipInfo {
        membershipPlanId
        membershipPlanIntroduce
      }
    }
  }
`;

/**
 * 获取猜你喜欢商品
 */
export const fetchGetGuessYouLikeProductsDocument = gql`
  query getGuessYouLikeProducts($orderId: ID!) {
    getGuessYouLikeProducts(orderId: $orderId) {
      guessProducts {
        productVariant {
          id
          sku
          price
          name
          options {
            code
            name
          }
          featuredAsset {
            source
          }
          customFields {
            virtualTargetType
          }
          product {
            id
            name
            enabled
            customFields {
              isVipProduct
            }
            featuredAsset {
              source
            }
            customFields {
              price
              markingPrice
            }
          }
        }
        unitPrice
        quantity
      }
      introduce
    }
  }
`;


/**
 * 订单加入猜你喜欢商品
 */
export const fetchAddItemToOrderByGuessYouLikeDocument = gql`
  mutation addItemToOrderByGuessYouLike($orderId: ID!, $productVariantId: ID!) {
    addItemToOrderByGuessYouLike(orderId: $orderId, productVariantId: $productVariantId)
  }
`;

/**
 * 订单移除猜你喜欢商品
 */
export const fetchRemoveItemFromOrderByGuessYouLikeDocument = gql`
  mutation removeItemFromOrderByGuessYouLike($orderId: ID!) {
    removeItemFromOrderByGuessYouLike(orderId: $orderId)
  }
`;

/**
 * 抽奖活动触发计算附加次数
 */
export const fetchCalculateTimeDocument = gql`
  mutation calculateTime($lotteryActivityId: ID!, $source: LotteryActivityTimeSource!, $sourceValue: String) {
    calculateTime(lotteryActivityId: $lotteryActivityId, source: $source, sourceValue: $sourceValue) {
      success
      count
      remainingTimes
      lotteryGuideText
    }
  }
`;

/**
 * 设置订单为快递配送
 */
export const setOrderDeliveryExpressDocument = gql`
  mutation setOrderDeliveryExpress($orderId: ID!) {
    setOrderDeliveryExpress(orderId: $orderId) {
      id
    }
  }
`;

/**
 * 设置订单为门店自提
 */
export const setOrderDeliveryPickupDocument = gql`
  mutation setOrderDeliveryPickup($orderId: ID!) {
    setOrderDeliveryPickup(orderId: $orderId) {
      id
    }
  }
`;
